import { useCallback, useState } from 'react'

import { DragDropHandlers, DropPosition, StructureTreeItem } from '@/components/TreeV2'
import { reorderNode } from '@/endpoints'
import { useApiRequest } from '@/endpoints/hooks'
import { ReorderStructureDto, StructureObjectDto } from '@/endpoints/models'
import { isNestableNodeType } from '@/utils/nodes'

export const TREE_PANEL_DRAG_DROP_NODE_ID = 'TREE_PANEL_DRAG_DROP_NODE_ID'

interface DragData {
	nodeId: number
	parentStructureId?: number
	type: string
}

export const useDragHandlerV2 = ({
	onSortingDisabled,
	expandedItems,
	onExpandItem,
	onRefreshTreeSilent,
}: {
	expandedItems: string[],
	onExpandItem?: (itemId: string) => void,
	/** Silent full tree refresh after move (e.g. to update names) */
	onRefreshTreeSilent?: () => Promise<void>,
	onSortingDisabled?: () => void
}) => {
	const request = useApiRequest()

	const [dragData, setDragData] = useState<DragData | null>(null)

	return useCallback(
		(item: StructureTreeItem): DragDropHandlers | null => {
			const nodeType = item.type
			const nodeId = item.structureId
			const canDropInside = isNestableNodeType(nodeType as StructureObjectDto.TypeEnum)

			const moveNode = async (
				dragDataMove: DragData,
				dropPosition: DropPosition,
			) => {
				const shouldMoveInside = dropPosition === 'inside'
				const parentStructureId = item.parentStructureId

				const targetParentStructureId = shouldMoveInside
					? nodeId
					: parentStructureId

				const enterBeforeStructureId = shouldMoveInside 
					? undefined 
					: dropPosition === 'before' 
						? nodeId 
						: undefined

				await request(
					reorderNode(dragDataMove.nodeId, {
						enterBeforeStructureId,
						targetParentStructureId,
						reorderStrategy: ReorderStructureDto.ReorderStrategyEnum.CUSTOM,
					}),
				)

				await onRefreshTreeSilent?.()

				if (shouldMoveInside) {
					onExpandItem?.(String(nodeId))
				}

				setDragData(null)
			}

			const onDragStart = (e: React.DragEvent) => {
				e.stopPropagation()
				e.dataTransfer.effectAllowed = 'all'
				e.dataTransfer.setData(TREE_PANEL_DRAG_DROP_NODE_ID, nodeId.toString())

				const dragDataStart: DragData = {
					nodeId,
					parentStructureId: item.parentStructureId,
					type: nodeType,
				}

				setDragData(dragDataStart)
			}

			const onDragOver = (e: React.DragEvent) => {
				e.preventDefault()
				e.stopPropagation()
			}

			let timerOpenNode: ReturnType<typeof setTimeout> | null = null

			// Open folder or system after hovering for a bit
			const openNode = () => {
				timerOpenNode = setTimeout(() => {
					if (!canDropInside) {
						return
					}

					const itemIdStr = String(nodeId)
					if (!expandedItems.includes(itemIdStr)) {
						onExpandItem?.(itemIdStr)
					}
				}, 1200)
			}

			const onDragEnter = (e: React.DragEvent) => {
				e.preventDefault()
				e.stopPropagation()
				openNode()
			}

			const onDragLeave = () => {
				if (timerOpenNode) {
					clearTimeout(timerOpenNode)
				}
			}

			const onDrop = (
				e: React.DragEvent,
				dropPosition: DropPosition,
			) => {
				e.preventDefault()
				e.stopPropagation()

				if (timerOpenNode) {
					clearTimeout(timerOpenNode)
				}

				onSortingDisabled?.()

				if (dragData) {
					moveNode(dragData, dropPosition)
				}
			}

			return {
				draggable: true,
				onDrop,
				onDragStart,
				onDragLeave,
				onDragEnter,
				onDragOver,
				canDropInside,
			}
		},
		[request, expandedItems, dragData, onSortingDisabled, onExpandItem, onRefreshTreeSilent],
	)
}
